import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CLOSING_NOTE_FEATURE_KEY, ClosingNoteState } from '../reducer/closing-note.reducer';
import { CommentCounts, TodoCounts } from '../components/notes-overview/notes-overview.types';

const feature = createFeatureSelector<ClosingNoteState>(CLOSING_NOTE_FEATURE_KEY);

export const selectComments = createSelector(feature, (s) => s.byKind.COMMENT);
export const selectTodos = createSelector(feature, (s) => s.byKind.TODO);
export const selectAccountNotes = createSelector(feature, (s) => s.accountNotes);

export const selectCommentsLoading = createSelector(feature, (s) => s.loading.COMMENT);
export const selectTodosLoading = createSelector(feature, (s) => s.loading.TODO);
export const selectCommentsError = createSelector(feature, (s) => s.error.COMMENT);
export const selectTodosError = createSelector(feature, (s) => s.error.TODO);
export const selectAccountLoading = createSelector(feature, (s) => s.accountLoading);
export const selectAccountError = createSelector(feature, (s) => s.accountError);

/** The Pendenzen of the open account (the account page's Pendenzen area). */
export const selectAccountTodos = createSelector(feature, (s) => s.accountTodos);
export const selectAccountTodosLoading = createSelector(feature, (s) => s.accountTodosLoading);
export const selectAccountTodosError = createSelector(feature, (s) => s.accountTodosError);

/** REV-21369 — die Kommentare des offenen Belegs (ADR-0021 E6). */
export const selectDocumentNotes = createSelector(feature, (s) => s.documentNotes);
export const selectDocumentLoading = createSelector(feature, (s) => s.documentLoading);
export const selectDocumentError = createSelector(feature, (s) => s.documentError);

export const selectDetail = createSelector(feature, (s) => s.detail);
export const selectDetailLoading = createSelector(feature, (s) => s.detailLoading);
export const selectDetailError = createSelector(feature, (s) => s.detailError);
/** ⚠️ Getrennt von `selectDetailError` — „gibt es nicht" ist kein Systemfehler (AC9). */
export const selectDetailMissing = createSelector(feature, (s) => s.detailMissing);

export const selectCommentFilter = createSelector(feature, (s) => s.commentFilter);
export const selectTodoStatus = createSelector(feature, (s) => s.todoStatus);
export const selectTodoAudience = createSelector(feature, (s) => s.todoAudience);
export const selectSearchTerm = createSelector(feature, (s) => s.searchTerm);
export const selectSort = createSelector(feature, (s) =>
    s.sortKey === null ? null : { key: s.sortKey as never, direction: s.sortDirection },
);

export const selectExpandedNoteIds = createSelector(feature, (s) => s.expandedNoteIds);
export const selectEditingNoteId = createSelector(feature, (s) => s.editingNoteId);
export const selectComposerKind = createSelector(feature, (s) => s.composerKind);
export const selectRecentlyChangedId = createSelector(feature, (s) => s.recentlyChangedId);
export const selectWriteError = createSelector(feature, (s) => s.writeError);

/**
 * Die Zahlen der Kommentar-Pillen (AC2).
 *
 * ⚠️ Gezaehlt wird ueber ALLE Kommentare, nicht ueber die gerade gefilterten: eine Pille, deren
 * Zahl sich mit der Auswahl aendert, sagt nicht mehr, wie viel sie oeffnen wuerde.
 */
export const selectCommentCounts = createSelector(
    selectComments,
    (notes): CommentCounts => ({
        all: notes.length,
        internal: notes.filter((n) => n.tags.indexOf('internal') !== -1).length,
        closingMeeting: notes.filter((n) => n.tags.indexOf('closingMeeting') !== -1).length,
    }),
);

/**
 * Die Zahlen der Pendenz-Pillen (AC3).
 *
 * ⚠️ Die Zielgruppen-Zahlen folgen der ERSTEN Wahl: unter „Erledigt" zaehlen sie erledigte
 * Pendenzen. Deshalb haengt dieser Selektor am Status — ohne ihn zeigte die Zielgruppen-Reihe
 * unter „Erledigt" die Zahlen der offenen.
 */
export const selectTodoCounts = createSelector(
    selectTodos,
    selectTodoStatus,
    (notes, status): TodoCounts => {
        const inStatus = notes.filter((n) => (status === 'done' ? n.doneAt !== null : n.doneAt === null));
        const withTag = (tag: string) => inStatus.filter((n) => n.tags.indexOf(tag) !== -1).length;
        return {
            open: notes.filter((n) => n.doneAt === null).length,
            done: notes.filter((n) => n.doneAt !== null).length,
            todo: withTag('todo'),
            reviewer: withTag('reviewer'),
            client: withTag('client'),
        };
    },
);
